import React, { useEffect, useState } from 'react';
import LoadError from '../../components/LoadError';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, API_BASE } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Building2, Search, MapPin, Globe, Trophy, GraduationCap, BookOpen, X, Filter } from 'lucide-react';
import ProgramsDialog from '../../components/ProgramsDialog';

const STREAMS = ['Bachelor', 'Masters', 'UG', 'PG', 'Dip', 'Pathway Programs'];

const getImageUrl = (url) => {
  if (!url) return '/university-fallback.jpg';
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
};

const QUICK_COUNTRIES = [
  'USA', 'UK', 'Australia', 'Canada', 'Germany', 'France', 'Ireland',
  'New Zealand', 'Singapore', 'Malaysia', 'Dubai', 'Netherlands',
  'Switzerland', 'Italy', 'Spain', 'Poland', 'Latvia', 'Japan',
];

export default function PartnerUniversities() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [displayCount, setDisplayCount] = useState(24); // drawing thousands of cards at once is slow on phones
  const [countryFilter, setCountryFilter] = useState(() => searchParams.get('country') || 'all');
  const [streamFilter, setStreamFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [countries, setCountries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [programsUni, setProgramsUni] = useState(null);
  const [loadingProgramsId, setLoadingProgramsId] = useState(null);

  // The list response no longer carries each university's full `programs`
  // array (see list_universities on the backend) — just a `programs_count`
  // for the badge. Fetch the one university's full detail (including
  // programs) only when its "View Programs" button is actually clicked.
  const openPrograms = async (uni) => {
    setLoadingProgramsId(uni.id);
    try {
      const res = await apiGet(`/universities/${uni.id}`);
      setProgramsUni(res.data);
    } catch (err) {
      toast.error('Failed to load programs');
    } finally {
      setLoadingProgramsId(null);
    }
  };

  useEffect(() => {
    apiGet('/universities/countries').then(r => setCountries(r.data)).catch(console.error);
    apiGet('/universities/courses').then(r => setCourses(r.data)).catch(console.error);
  }, []);

  // Wait until typing pauses, and cancel stale requests so an older, slower
  // response can never overwrite the newer list (same as UniversityManagement).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (countryFilter && countryFilter !== 'all') params.set('country', countryFilter);
    if (streamFilter && streamFilter !== 'all') params.set('stream', streamFilter);
    if (courseFilter && courseFilter !== 'all') params.set('course', courseFilter);
    const qs = params.toString();
    apiGet(`/universities${qs ? '?' + qs : ''}`, { signal: controller.signal })
      .then(r => setUniversities(r.data))
      .catch(err => { if (err.code !== 'ERR_CANCELED' && err.name !== 'CanceledError') setLoadError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [debouncedSearch, countryFilter, streamFilter, courseFilter, attempt]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="partner-universities-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Universities</h1>
          <p className="text-ink-2 mt-1">Browse partner universities worldwide</p>
        </div>
      </div>

      {/* Quick country filter tiles */}
      {countries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {[...QUICK_COUNTRIES.filter(qc => countries.some(c => c.toLowerCase() === qc.toLowerCase())), ...countries.filter(c => c && !QUICK_COUNTRIES.some(qc => qc.toLowerCase() === c.toLowerCase()))].map(qc => {
            const active = countryFilter.toLowerCase() === qc.toLowerCase();
            return (
              <button
                key={qc}
                onClick={() => setCountryFilter(active ? 'all' : qc)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  active
                    ? 'bg-brand text-white border-brand shadow-sm'
                    : 'bg-surface text-ember border-contrast/10 hover:border-brand hover:text-brand hover:bg-surface-2'
                }`}
              >
                <span>{qc}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Country / Stream / Course filters, then Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="w-full sm:w-52 h-11" data-testid="country-filter" aria-label="Filter by country">
            <Filter className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Country</SelectItem>
            {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={streamFilter} onValueChange={setStreamFilter}>
          <SelectTrigger className="w-full sm:w-56 h-11" data-testid="stream-filter" aria-label="Filter by level of education">
            <GraduationCap className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="Level of Education" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Level of Education</SelectItem>
            {STREAMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-full sm:w-52 h-11" data-testid="course-filter" aria-label="Filter by program">
            <BookOpen className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="Program" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Program</SelectItem>
            {courses.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md sm:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search universities..." data-testid="uni-search" className="pl-10 h-11 border-contrast/10" />
        </div>
        {(countryFilter !== 'all' || streamFilter !== 'all' || courseFilter !== 'all') && (
          <div className="flex items-center gap-2 flex-wrap">
            {countryFilter !== 'all' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-surface-3 text-brand rounded-lg text-sm font-medium">
                {countryFilter}
                <button onClick={() => setCountryFilter('all')} aria-label="Clear country filter" className="hover:text-brand-soft"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {streamFilter !== 'all' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-ember/10 text-warning rounded-lg text-sm font-medium">
                {streamFilter}
                <button onClick={() => setStreamFilter('all')} aria-label="Clear education level filter" className="hover:text-warning"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {courseFilter !== 'all' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-brand/10 text-success rounded-lg text-sm font-medium">
                {courseFilter}
                <button onClick={() => setCourseFilter('all')} aria-label="Clear program filter" className="hover:text-success"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Count */}
      <div className="flex items-center gap-2 text-sm text-ink-2">
        <Building2 className="w-4 h-4" />
        <span>{universities.length} universit{universities.length === 1 ? 'y' : 'ies'}{countryFilter !== 'all' ? ` in ${countryFilter}` : ''}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>
      ) : loadError ? (
        <LoadError what="the universities" onRetry={() => setAttempt(a => a + 1)} />
      ) : universities.length === 0 ? (
        <div className="bg-surface rounded-xl border border-contrast/[0.07] shadow-card p-12 text-center">
          <Building2 className="w-12 h-12 text-ink-3 mx-auto mb-4" />
          <p className="text-ink-2">No universities found{countryFilter !== 'all' ? ` in ${countryFilter}` : ''}</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="universities-grid">
          {universities.slice(0, displayCount).map((uni) => (
            <div key={uni.id} className="bg-surface rounded-2xl border border-contrast/[0.07] hover:-translate-y-1 transition-all duration-200 overflow-hidden flex flex-col h-full" data-testid={`uni-card-${uni.id}`}>
              {/* Cover area */}
              <div className="relative w-full bg-sunken flex items-end overflow-hidden h-40 shrink-0">
                <img
                  src={getImageUrl(uni.cover_image_url)}
                  alt={uni.name + " Campus"}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={e => { e.target.src = '/university-fallback.jpg'; }}
                />
                
                <span className="absolute top-3 right-3 text-xs px-2 py-0.5 bg-surface/90 backdrop-blur-md text-ember rounded-full font-medium border border-contrast/10 shadow-sm z-20">{uni.country}</span>
              </div>
              {/* Info strip */}
              <div className="border-t border-contrast/[0.07] p-5 flex flex-col flex-grow gap-2">
                <h3 
                  title={uni.name}
                  className="line-clamp-2 min-h-[2.75rem] text-sm md:text-base font-semibold leading-snug tracking-tight text-ink" 
                  style={{ fontFamily: 'Outfit' }}
                >
                  {uni.name}
                </h3>
                
                <div className="flex flex-col gap-1.5">
                  <p className="flex items-center gap-1.5 text-xs text-ink-3 truncate" title={uni.city ? `${uni.city}, ${uni.country}` : uni.country}>
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{uni.city ? `${uni.city}, ` : ''}{uni.country}</span>
                  </p>
                  
                  {uni.ranking > 0 && (
                    <p className="flex items-center gap-1.5 text-xs text-ink-3 truncate">
                      <Trophy className="w-3 h-3 shrink-0" />
                      <span className="truncate">Rank #{uni.ranking}</span>
                    </p>
                  )}
                  
                  {uni.website && (
                    <a href={uni.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-brand hover:underline truncate" title={uni.website}>
                      <Globe className="w-3 h-3 shrink-0" />
                      <span className="truncate">{uni.website}</span>
                    </a>
                  )}
                </div>
                
                <div className="mt-auto pt-3 flex flex-col gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    {uni.stream && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-surface-2/80 text-ink-2 border border-contrast/10 whitespace-nowrap">
                        {uni.stream}
                      </span>
                    )}
                    {uni.courses?.length > 0 && (
                      <>
                        {uni.courses.slice(0, 3).map((c, i) => (
                          <span key={i} title={c} className="text-[11px] px-2 py-0.5 rounded-md bg-surface-2/80 text-ink-2 border border-contrast/10 truncate max-w-[120px]">
                            {c}
                          </span>
                        ))}
                        {uni.courses.length > 3 && (
                          <span className="text-[11px] px-2 py-0.5 rounded-md bg-surface-2/80 text-ink-2 border border-contrast/10">
                            +{uni.courses.length - 3}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {uni.programs_count > 0 && (
                    <button
                      onClick={() => openPrograms(uni)}
                      disabled={loadingProgramsId === uni.id}
                      data-testid={`view-programs-${uni.id}`}
                      className="inline-flex items-center gap-1.5 self-start text-[11px] font-medium px-2.5 py-1 rounded-md bg-ember/10 text-warning border border-ember/30 hover:bg-ember/20 transition-colors disabled:opacity-60"
                    >
                      <GraduationCap className="w-3 h-3" /> {loadingProgramsId === uni.id ? 'Loading...' : `${uni.programs_count} Program${uni.programs_count === 1 ? '' : 's'}`}
                    </button>
                  )}
                  <Button
                    size="sm"
                    className="w-full bg-brand hover:bg-brand/90 text-white text-xs rounded-lg"
                    onClick={() => navigate(`/partner/candidates/new?university_id=${uni.id}&country=${encodeURIComponent(uni.country || '')}`)}
                    data-testid={`apply-uni-${uni.id}`}
                  >
                    Apply Now
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {universities.length > displayCount && (
          <div className="flex justify-center pt-4 pb-8">
            <Button
              onClick={() => setDisplayCount(prev => prev + 24)}
              className="bg-surface border border-contrast/10 shadow-card text-ink-2 hover:text-ink hover:bg-surface-2 px-8 rounded-full"
            >
              Load More Universities
            </Button>
          </div>
        )}
        </>
      )}

      <ProgramsDialog
        open={!!programsUni}
        onOpenChange={(o) => !o && setProgramsUni(null)}
        university={programsUni}
      />
    </div>
  );
}
